import { v4 as uuidv4 } from 'uuid';
import { normalizeBrowserUrl } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';

/**
 * Drop product references the model over-extracted from EXAMPLE text — names that
 * appear in the prompt right after an example marker ("e.g.", "such as", …). Those
 * are descriptive context (e.g. "Visual: … (e.g., Earthly Embrace bar on stone …)"),
 * not products to look up, so they'd otherwise trigger a spurious "I couldn't find …"
 * for something the user never asked to feature. Deterministic backstop to the
 * prompt-side extraction rules. (POR-13481)
 *
 * @param {Array} productReferences
 * @param {string} promptText  the full user prompt (image_prompt)
 * @returns {Array}
 */
export function stripExampleReferences(productReferences, promptText) {
  if (!productReferences?.length || !promptText) {
    return productReferences || [];
  }

  const text = String(promptText);
  const markerRe = /(?:\be\.?\s*g\.?|\b(?:such as|for example|for instance)\b)[,:\s]*/gi;
  const exampleSpans = [];
  let m;
  while ((m = markerRe.exec(text)) !== null) {
    const rest = text.slice(m.index + m[0].length);
    const endIdx = rest.search(/[)\].;\n]/);
    exampleSpans.push((endIdx === -1 ? rest : rest.slice(0, endIdx)).toLowerCase());
  }

  if (!exampleSpans.length) {
    return productReferences;
  }

  return productReferences.filter((ref) => {
    const name = String(ref.display_name || ref.value || '').toLowerCase().trim();
    // Keep resolvable/explicit refs (attachments, URLs) and very short tokens as-is.
    if (!name || name.length < 3 || ref.type === 'attachment' || ref.type === 'url' || ref.type === 'product_asset') {
      return true;
    }
    return !exampleSpans.some((span) => span.includes(name));
  });
}

/**
 * Resolve attachment-type product references to URL-type using savedAttachments from the AI response.
 * Also resolves 'last_generated' references by searching the chat history.
 *
 * @param {Array} productReferences - [{type, value, display_name}]
 * @param {Array} savedAttachments - [{payload, type, ...}] from the AI response
 * @param {Array} messages - [{role, content, attachments, ...}] full chat history
 * @returns {Array} resolved product references with attachments/generated images converted to URLs
 */
export function resolveAttachmentReferences(productReferences, savedAttachments, messages = [], skipFallback = false, excludeAttachmentIndices = []) {
  if (!productReferences?.length) {
    return productReferences || [];
  }

  // Collect all available attachment URLs from multiple sources:
  // 1. savedAttachments from the API response
  // 2. The last user message's attachments (original files before backend processing)
  const allAttachmentUrls = [];

  if (savedAttachments?.length) {
    savedAttachments.forEach((att) => {
      const url = typeof att === 'string' ? att : att?.payload;
      if (url) {
        allAttachmentUrls.push(url);
      }
    });
  }

  // Fallback: get attachments from the last user message if savedAttachments didn't provide URLs
  if (!allAttachmentUrls.length && messages.length) {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMsg?.attachments?.length) {
      lastUserMsg.attachments.forEach((att) => {
        const url = typeof att === 'string' ? att : (att?.payload || att?.preview);
        if (url) {
          allAttachmentUrls.push(url);
        }
      });
    }
  }

  const usedIndices = new Set();

  // First pass: resolve explicit attachment and last_generated references
  const resolved = productReferences.map((ref) => {
    if (ref.type === 'attachment' && allAttachmentUrls.length) {
      const index = parseInt(ref.value, 10);
      if (index < allAttachmentUrls.length) {
        usedIndices.add(index);
        return { ...ref, type: 'url', value: allAttachmentUrls[index] };
      }
    }

    if (ref.type === 'last_generated' && messages.length) {
      const reversed = [...messages].reverse();
      for (const msg of reversed) {
        if (msg.role === 'assistant' && msg.attachments?.length) {
          const img = msg.attachments.find((a) => a.type === 'url');
          if (img?.payload) {
            return {
              ...ref,
              type: 'url',
              value: img.payload,
              display_name: ref.display_name || img.name || 'Last Image',
            };
          }
        }
      }
    }

    return ref;
  });

  // Second pass: if there are unused attachments and name-type refs,
  // the user likely attached images for those products — use them directly
  let unusedUrls = [];
  let unusedIdx = 0;
  if (!skipFallback && allAttachmentUrls.length) {
    unusedUrls = allAttachmentUrls.filter((_, i) => !usedIndices.has(i) && !excludeAttachmentIndices.includes(i));
  }

  const secondPass = resolved.map((ref) => {
    if ((ref.type === 'name' || ref.type === 'gtin') && unusedIdx < unusedUrls.length) {
      return { ...ref, type: 'url', value: unusedUrls[unusedIdx++] };
    }
    return ref;
  });

  // Third pass: if a reference is still an ID (asin/gtin/name), try to resolve it
  // from the chat history if we previously found a matching product image.
  return secondPass.map((ref) => {
    if ((ref.type === 'asin' || ref.type === 'gtin' || ref.type === 'name' || ref.type === 'salsify') && messages.length) {
      const reversed = [...messages].reverse();
      for (const msg of reversed) {
        const found = msg?._foundProducts || [];
        const hit = found.find((p) => p.name === ref.value || p.name === ref.display_name);
        if (hit?.url) {
          return { ...ref, type: 'url', value: hit.url };
        }
      }
    }
    return ref;
  });
}

/**
 * Bridge a Vuex context ({commit, dispatch}) to the chat-surface adapter
 * interface (addMessage / saveMessages / setProcessing). Lets the handlers in
 * this file work the same way for both surfaces (CS Vuex actions and the
 * sidebar's adapter-style handlers).
 */
function toChatAdapter(ctxOrAdapter) {
  if (ctxOrAdapter && typeof ctxOrAdapter.addAndSaveMessage === 'function') {
    return ctxOrAdapter;
  }

  const { commit, dispatch } = ctxOrAdapter;

  return {
    addMessage: (msg) => commit('ADD_MESSAGE', msg),
    saveMessages: (msgOrMessages) => dispatch('saveMessages', msgOrMessages),
    addAndSaveMessage: (msg) => dispatch('addAndSaveMessage', msg),
    addAndSaveMessages: (messages) => dispatch('addAndSaveMessages', messages),
    setProcessing: (flag) => commit('SET_PROCESSING_CHAT_MESSAGE', flag),
  };
}

/**
 * Search for product images in the library and show results in chat.
 *
 * @param {Array} productReferences - [{type, value, display_name}]
 * @param {Object} ctxOrAdapter - Vuex context {commit, dispatch} OR a chat
 *   adapter exposing addMessage/saveMessages/setProcessing.
 * @returns {Promise<boolean>} true if all products were found (or no search needed)
 */
export async function searchAndShowProductImages(productReferences, ctxOrAdapter, options = {}) {
  const originalCtx = ctxOrAdapter;
  const adapter = toChatAdapter(ctxOrAdapter);
  const { skipSpinner = false } = options;
  if (!skipSpinner) adapter.setProcessing(true);
  // Show already-resolved URL-type refs (from user attachments) before searching
  const alreadyResolved = (productReferences || []).filter(
    (ref) => (ref.type === 'url' || ref.type === 'product_asset') && ref.display_name
  );

  alreadyResolved.forEach((ref) => {
    if (!ref.id) ref.id = uuidv4();
  });

  if (alreadyResolved.length > 0) {
    // Build initial reference payload that the backend expects
    const refsForBackend = alreadyResolved.map((r) => ({
      id: r.id,
      type: r.type,
      value: r.value,
      display_name: r.display_name,
    }));

    // Trigger backend processing (best-effort; if it fails, we still show originals)
    const processedMap = {};
    try {
      if (originalCtx && typeof originalCtx.dispatch === 'function') {
        const processed = await originalCtx.dispatch('processProductImagesForCutout', {
          endpoint: '/networks/process-product-images',
          references: refsForBackend,
        });
        (processed || []).forEach((p) => { processedMap[p.reference_id] = p; });
      }
    } catch (_) {
      // ignore — render originals
    }

    const niceName = (r) => {
      const n = r.display_name;
      if (typeof n !== 'string' || n.trim() === '' || /^\d+$/.test(n.trim())) return 'Attached image';
      return n;
    };

    await adapter.addAndSaveMessage({
      role: 'assistant',
      content: `Using attached ${alreadyResolved.length === 1 ? 'image' : 'images'} for: <b>${alreadyResolved.map(niceName).join('</b>, <b>')}</b>`,
      widgets: [
        {
          type: 'resolved_product_cards',
          id: 'product_resolved_cards',
          references: alreadyResolved.map((r) => {
            const meta = processedMap[r.id] || {};
            return {
              id: r.id,
              display_name: r.display_name,
              original_url: meta.original_url || (r.type === 'product_asset' ? '/share?file=' + r.value : r.value),
              cutout_url: meta.cutout_url || null,
              was_auto_cut: Boolean(meta.was_auto_cut),
              keep_background: false,
              _deselected: false,
            };
          }),
        },
      ],
    });
  }

  const searchable = (productReferences || []).filter(
    (ref) => ref.type === 'name' || ref.type === 'gtin' || ref.type === 'asin' || ref.type === 'salsify'
  );

  if (!searchable.length) {
    if (!skipSpinner) adapter.setProcessing(false);
    return true;
  }

  let endpoint = '/networks/search-product-images';

  if (searchable.some((ref) => ref.type === 'salsify')) {
    endpoint = '/networks/search-salsify-images';
  }

  try {
    const { data } = await axios.post(endpoint, {
      product_references: searchable,
    });

    if (data.error === 'invalid_salsify_credentials') {
      await adapter.addAndSaveMessage({
        role: 'assistant',
        content: 'Your Salsify credentials appear to be invalid. Please contact support@itsrapid.io to fix this.',
      });
      return false;
    }

    if (data.error === 'no_salsify_connection') {
      await adapter.addAndSaveMessage({
        role: 'assistant',
        content: 'Would you like to setup a Salsify connection?',
        widgets: [{
          type: 'action_buttons',
          id: 'salsify_setup',
          buttons: [
            { label: 'Yes', action: 'setup_salsify_yes', variant: 'primary' },
            { label: 'No', action: 'setup_salsify_no', variant: 'secondary' },
          ],
        }],
      });
      return false;
    }

    const results = data.results || [];
    const found = results.filter((r) => r.found);
    const notFound = results.filter((r) => !r.found);

    // Found ASINs get their own per-ref message ("I fetched the hero image
    // for <ASIN>") so the user gets a clear, individual confirmation. Other
    // ref types (gtin/name) stay grouped in one message.
    const foundAsins = found.filter((r) => r.type === 'asin');
    const foundSalsify = found.filter((r) => r.type === 'salsify');
    const foundOthers = found.filter((r) => r.type !== 'asin' && r.type !== 'salsify');

    foundAsins.forEach((r) => {
      // The backend returns share URLs that may be rooted at an internal host
      // (e.g. http://nginx/share?file=...), unreachable from the browser.
      // normalizeBrowserUrl rewrites them onto the current window.origin.
      const url = normalizeBrowserUrl(r.images[0]);
      const cardHtml = `<div style="margin-top:8px;">`
        + `<img src="${url}" style="max-height:200px;border-radius:8px;display:block;" onerror="this.style.display='none'">`
        + `</div>`;

      adapter.addAndSaveMessage({
        role: 'assistant',
        content: `I fetched the hero image for <b>${r.display_name}</b>${cardHtml}`,
        _foundProducts: [{ id: uuidv4(), name: r.display_name, url }],
      });
    });

    foundSalsify.forEach((r) => {
      const url = normalizeBrowserUrl(r.images[0]);

      const cardHtml = `<div style="margin-top:8px;">`
        + `<img src="${url}" style="max-height:200px;border-radius:8px;display:block;" onerror="this.style.display='none'">`
        + `</div>`;

      adapter.addAndSaveMessage({
        role: 'assistant',
        content: `I fetched the primary image for <b>${r.display_name}</b>${cardHtml}`,
        _foundProducts: [{ id: uuidv4(), name: r.display_name, url }],
      });
    });

    if (foundOthers.length > 0) {
      const foundOthersWithIds = foundOthers.map((r) => ({ ...r, _id: uuidv4(), _url: normalizeBrowserUrl(r.images[0]) }));

      const refsForBackend = foundOthersWithIds.map((p) => ({
        id: p._id,
        type: 'url',
        value: p._url,
        display_name: p.display_name,
      }));

      const processedMap = {};
      try {
        if (originalCtx && typeof originalCtx.dispatch === 'function') {
          const processed = await originalCtx.dispatch('processProductImagesForCutout', {
            endpoint: '/networks/process-product-images',
            references: refsForBackend,
          });
          (processed || []).forEach((p) => { processedMap[p.reference_id] = p; });
        }
      } catch (_) {
        // ignore — render originals
      }

      const niceName = (r) => {
        const n = r.display_name;
        if (typeof n !== 'string' || n.trim() === '' || /^\d+$/.test(n.trim())) return 'Attached image';
        return n;
      };

      await adapter.addAndSaveMessage({
        role: 'assistant',
        content: `I found ${foundOthers.length === 1 ? 'an image' : 'images'} for: <b>${foundOthers.map(niceName).join('</b>, <b>')}</b>`,
        _foundProducts: foundOthersWithIds.map((r) => ({ id: r._id, name: r.display_name, url: r._url })),
        widgets: [
          {
            type: 'resolved_product_cards',
            id: 'product_resolved_cards_found',
            references: foundOthersWithIds.map((r) => {
              const meta = processedMap[r._id] || {};
              return {
                id: r._id,
                display_name: r.display_name,
                original_url: meta.original_url || r._url,
                cutout_url: meta.cutout_url || null,
                was_auto_cut: Boolean(meta.was_auto_cut),
                keep_background: false,
                _deselected: false,
              };
            }),
          },
        ],
      });
    }

    if (notFound.length > 0) {
      const notFoundSalsify = notFound.filter((r) => r.type === 'salsify');
      const notFoundOthers = notFound.filter((r) => r.type !== 'salsify');

      for (const r of notFoundSalsify) {
        await adapter.addAndSaveMessage({
          role: 'assistant',
          content: `I didn't find <b>${r.display_name}</b> in Salsify. Would you like to search for another product ID?`,
          _missingProductNames: [r.display_name],
          widgets: [{
            type: 'action_buttons',
            id: 'salsify_not_found',
            buttons: [
              { label: 'Search with another ID', action: 'retry_salsify_search', variant: 'primary' },
              { label: 'Continue without', action: 'skip_missing_products', variant: 'secondary' },
            ],
          }],
        });
      }

      if (notFoundOthers.length > 0) {
        const names = notFoundOthers.map((r) => r.display_name);
        const hasAsins = notFoundOthers.some((r) => r.type === 'asin');

        let errorMessage = `I couldn't find images for <b>${names.join('</b>, <b>')}</b> in the product library.`;

        if (hasAsins) {
          errorMessage = `I couldn't find images for <b>${names.join('</b>, <b>')}</b> in Amazon.`;
        }

        await adapter.addAndSaveMessage({
          role: 'assistant',
          content: errorMessage,
          _missingProductNames: names,
          widgets: [
            {
              type: 'action_buttons',
              id: 'product_not_found',
              buttons: [
                {
                  label: 'Generate with AI',
                  action: 'generate_missing_products',
                  variant: 'primary',
                },
                {
                  label: 'Select images',
                  action: 'select_missing_products',
                  variant: 'secondary',
                },
                {
                  label: 'Continue without',
                  action: 'skip_missing_products',
                  variant: 'secondary',
                },
              ],
            },
          ],
        });
      }

      return false;
    }
    return true;
  } catch (e) {
    console.warn('Product image search failed:', e);
    return true;
  } finally {
    if (!skipSpinner) adapter.setProcessing(false);
  }
}

/**
 * Generate a background preview and show it in chat for user selection.
 *
 * @param {string} backgroundScene - The scene description for the background
 * @param {Object} context - Vuex context {commit, dispatch}
 */
export async function generateAndShowBackgroundPreviews(backgroundScene, { commit, dispatch }, size) {
  if (!backgroundScene) {
    return;
  }

  commit('SET_PROCESSING_CHAT_MESSAGE', true);

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: 'Generating a background for you...',
  });

  const LOADING_MESSAGES = [
    'Mixing the paint...',
    'Polishing the details...',
    'Elevating the visual storytelling...',
    'Arranging pixels artistically...',
    'Converting caffeine into content...',
    'Making the impossible look easy...',
    'Performing minor acts of graphic wizardry...',
    'Teaching pixels new tricks...',
  ];

  const funTempId = `tmp_fun_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  commit('ADD_MESSAGE', { role: 'assistant', content: LOADING_MESSAGES[0], tempId: funTempId });

  let cycleIdx = 0;
  
  const intervalId = setInterval(() => {
    cycleIdx = (cycleIdx + 1) % LOADING_MESSAGES.length;
    commit('UPDATE_MESSAGE_CONTENT', { tempId: funTempId, content: LOADING_MESSAGES[cycleIdx] });
  }, 1200);

  try {
    const payload = { background_scene: backgroundScene };
    if (size) {
      payload.size = `${size.width}x${size.height}`;
    }
    const { data } = await axios.post('/networks/generate-background-previews', payload);

    const options = data.options || [];
    if (!options.length) {
      return;
    }

    const fullUrls = options.map((url) =>
      url.startsWith('http') ? url : window.location.origin + url
    );

    const imageHtml = `<div style="text-align:center;"><img src="${fullUrls[0]}" style="max-width:100%;border-radius:8px;"></div>`;

    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: imageHtml,
      _backgroundOptions: fullUrls,
      _backgroundScene: backgroundScene,
      _backgroundSize: size || null,
      widgets: [
        {
          type: 'action_buttons',
          id: 'bg_preview_select',
          buttons: [
            {
              label: 'Use this image',
              action: 'select_bg:0',
              variant: 'primary',
            },
            {
              label: 'Regenerate',
              action: 'request_bg_description',
              variant: 'secondary',
            },
          ],
        },
      ],
    });

  } catch (e) {
    console.warn('Background preview generation failed:', e);
    await dispatch('addAndSaveMessage', {
      role: 'assistant',
      content: 'Could not generate background previews. A background will be generated when creating the layout.',
    });
  } finally {
    clearInterval(intervalId);
    commit('REMOVE_MESSAGE', { tempId: funTempId });
    commit('SET_PROCESSING_CHAT_MESSAGE', false);
  }
}

export async function showAttachmentBackground(imageUrl, { dispatch }) {
  const fullUrl = imageUrl.startsWith('http') ? imageUrl : window.location.origin + imageUrl;
  const imageHtml = `<div style="text-align:center;"><img src="${fullUrl}" style="max-width:100%;border-radius:8px;"></div>`;

  await dispatch('addAndSaveMessage', {
    role: 'assistant',
    content: imageHtml,
    _backgroundOptions: [fullUrl],
    _backgroundScene: null,
    widgets: [
      {
        type: 'action_buttons',
        id: 'bg_preview_select',
        buttons: [
          {
            label: 'Use this image',
            action: 'select_bg:0',
            variant: 'primary',
          },
          {
            label: 'Regenerate',
            action: 'request_bg_description',
            variant: 'secondary',
          },
        ],
      },
    ],
  });
}
